'use client';

import './alert.scss';

import {
  Dispatch,
  type JSX,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useIntersection } from 'react-use';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { DisplayPurposeProps } from '@sola/schema/dotcom/shared/display.js';
import { TrackError } from '@sola/tracking/components/datalayer.js';

import { ButtonStyle } from '../../atoms/button-style/button-style';
import { CloseButton } from '../../atoms/close-button/close-button';
import { LinkStyle } from '../../atoms/link-style/link-style';
import { cn } from '../../utils/helpers';
import useReducedMotion from '../../utils/hooks/useReducedMotion';

const AlertDismiss = (
  setDismissed: Dispatch<SetStateAction<boolean>>,
  callback?: () => void,
) => {
  setDismissed(true);

  if (callback) {
    callback();
  }
};

export interface AlertProps extends Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'title' | 'children'
> {
  variant?: DisplayPurposeProps['variants'];
  title?: ReactNode;
  className?: string;
  message?: ReactNode;
  middleColumn?: ReactNode;
  /**
   * This should either be an anchor or button element that will be a child of <LinkStyle>
   * with the exception of using isFocus, which will use <ButtonStyle>
   */
  action?: JSX.Element;
  buttonAction?: JSX.Element;
  onClose?: () => void;
  noIcon?: boolean;
  dismissible?: boolean;
  closable?: boolean;
  scrollMobileOnly?: boolean;
  scrollIntoView?: boolean;
  scrollBehavior?: 'auto' | 'instant' | 'smooth';
  scrollPosition?: 'center' | 'end' | 'nearest' | 'start';
  mobileCentered?: boolean;
}

/**
 * Component for rendering alerts, which maintains its own dismissed state.
 */
export const Alert: React.FC<AlertProps> = ({
  variant = 'neutral',
  className,
  title,
  message,
  middleColumn,
  action,
  buttonAction,
  onClose,
  noIcon,
  dismissible = false,
  closable = false,
  scrollMobileOnly = false,
  scrollIntoView = false,
  scrollBehavior = 'smooth',
  scrollPosition = 'start',
  mobileCentered,
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [hasSeen, setHasSeen] = useState(false);
  const alertRef = useRef<HTMLDivElement | null>(null);
  const { intersectionRatio } =
    useIntersection(alertRef as React.RefObject<HTMLElement>, {
      root: null,
      rootMargin: '0px',
      threshold: 1,
    }) ?? {};
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!scrollIntoView || (scrollMobileOnly && window.innerWidth >= 768)) {
      return;
    }

    if (alertRef.current && !hasSeen && intersectionRatio !== 0) {
      alertRef.current?.scrollIntoView({
        behavior: reducedMotion ? 'auto' : scrollBehavior,
        block: scrollPosition,
      });

      setHasSeen(true);
    }
  }, []);

  if (dismissed) {
    return;
  }

  return (
    <div
      ref={alertRef}
      className={cn(
        `Alert Alert--${variant} relative flex items-start justify-between`,
        className,
      )}
    >
      {variant === 'negative' && (
        <TrackError
          errorMessage={title ? title.toString() : message?.toString()}
        />
      )}
      <div
        className={cn('w-full p-comfy-md flex gap-comfy-xs items-center', {
          Alert__wrapper: dismissible,
          'pr-snug-xl': !buttonAction,
          'flex-col md:flex-row text-center md:text-left': mobileCentered,
        })}
      >
        {!noIcon && (
          <div
            className={cn('Alert__icon md:align-start', {
              'align-start': !mobileCentered,
            })}
          >
            {
              {
                neutral: <ArkIcon iconName="info-circle" />,
                positive: <ArkIcon iconName="check-circle" />,
                warning: <ArkIcon iconName="alert-triangle" />,
                negative: <ArkIcon iconName="alert-circle" />,
              }[variant]
            }
          </div>
        )}
        <div
          className={cn(
            'Alert__content items-center gap-comfy-sm justify-between',
            { 'grow-1': !middleColumn },
          )}
        >
          {title && (
            <div className="Alert__title label-md label-potent">{title}</div>
          )}
          {message && (
            <div className="Alert__message label-md label-moderate mt-snug-md">
              {message}
            </div>
          )}
          {dismissible && (
            <div
              className={cn('Alert__btn-close mt-comfy-sm md:mr-comfy-sm', {
                'mr-comfy-sm': !mobileCentered || action,
              })}
            >
              <LinkStyle purpose="neutral" noUnderline noPadding>
                <button
                  type="button"
                  onClick={() => AlertDismiss(setDismissed, onClose)}
                >
                  {t('core.actions.dismiss')}
                </button>
              </LinkStyle>
            </div>
          )}
          {action && (
            <div className="Alert__btn-action mt-comfy-sm mr-comfy-sm">
              <LinkStyle noUnderline noPadding>
                {action}
              </LinkStyle>
            </div>
          )}
        </div>
        {middleColumn && <div className="grow-1">{middleColumn}</div>}
        {buttonAction && (
          <ButtonStyle
            purpose={variant}
            size="md"
            className="inline-block w-full md:w-fit h-fit"
          >
            {buttonAction}
          </ButtonStyle>
        )}
      </div>
      {(closable || dismissible) && (
        <CloseButton
          className="mt-snug-xl mr-snug-xl"
          onClick={() => AlertDismiss(setDismissed, onClose)}
          aria-label={t('core.alert.ariaLabel')}
        />
      )}
    </div>
  );
};
